import React from "react";

import {
  Button,
  Modal,
  ModalContent,
  ModalContentBody,
  ModalContentFooter,
  ModalContentHeader,
  Text,
} from "@zapier/design-system";

import { ChangeType, PlanV2 } from "@zapier/checkout/esm/types";

const getHeadingText = (changeType: ChangeType) => {
  switch (changeType) {
    case "upgrade":
      return "Thanks for upgrading!";

    case "downgrade":
      return "Downgrade complete";

    case "change":
      return "Change complete";

    default:
      return "";
  }
};

const buildContent = ({
  changeType,
  newPlan,
}: {
  changeType: ChangeType;
  newPlan: PlanV2;
}) => {
  let planStatement = "";
  let payingStatement = "";

  if (newPlan.allowCompanies || newPlan.hasTeams) {
    planStatement = `You just created a ${newPlan.type}. Well done`;
  } else {
    planStatement = `You're now on the ${newPlan.name} plan`;
    payingStatement =
      newPlan.amount === 0 ? "" : `paying ${newPlan.interval}ly`;
  }

  const punctuation = changeType === "upgrade" ? "!" : ".";

  return `${planStatement} ${payingStatement}${punctuation}`.replace(
    ` ${punctuation}`,
    punctuation
  );
};

const getPrimaryButtonProps = ({
  hasPausedZaps,
  newPlan,
}: {
  hasPausedZaps: boolean;
  newPlan: PlanV2;
}) => {
  if (newPlan.allowCompanies) {
    return {
      href: "/app/settings/organization",
      text: "Start setup",
    };
  }

  if (newPlan.hasTeams) {
    return {
      href: "/app/settings/members",
      text: "Invite team members",
    };
  }

  if (hasPausedZaps) {
    return {
      href: "/app/zaps/folder/paused",
      text: "View paused Zaps",
    };
  }

  return {
    href: "/app/zaps",
    text: "Go to my Zaps",
  };
};

const getSecondaryButtonProps = ({
  hasPausedZaps,
  newPlan,
}: {
  hasPausedZaps: boolean;
  newPlan: PlanV2;
}) => {
  if (newPlan.allowCompanies) {
    return {
      href: "/app/dashboard",
      text: "Go to dashboard",
    };
  }

  if (newPlan.hasTeams) {
    return {
      href: "/app/zaps",
      text: "Go to team Zaps",
    };
  }

  if (!hasPausedZaps) {
    return {
      href: "/app/billing/plans",
      text: "Go to plans and pricing",
    };
  }

  return null;
};

type Props = {
  /** The ChangeType for this plan change (`upgrade`, `downgrade` or `change`) */
  changeType: ChangeType;
  /** Indicates whether the plan change paused a user's zaps */
  hasPausedZaps?: boolean;
  /** The new plan the user has changed to */
  newPlan: PlanV2;
};

export const ChangePlanCompleteModal = ({
  changeType,
  hasPausedZaps = false,
  newPlan,
}: Props) => {
  const heading = getHeadingText(changeType);

  const content = buildContent({
    changeType,
    newPlan,
  });

  const secondaryButtonProps = getSecondaryButtonProps({
    hasPausedZaps,
    newPlan,
  });

  const primaryButtonProps = getPrimaryButtonProps({
    hasPausedZaps,
    newPlan,
  });

  /**
   * TODO: Can we adjust Apollo caching policies in a way that doesn't require
   * us to do a full page refresh here?
   */
  const navigateTo = (href: string) => {
    window.location.href = href; // Hard page reload to pull in new plan data.
  };

  return (
    <Modal ariaLabel={heading} canClose={false} onClosed={() => {}}>
      <ModalContent maxWidth="630px">
        <ModalContentHeader align="center">
          <Text tag="h1" type="paragraph1ShortBold">
            <span id="modal-title">{heading}</span>
          </Text>
        </ModalContentHeader>

        <ModalContentBody align="center">
          <Text type="paragraph3">
            <span id="modal-description">{content}</span>
          </Text>
        </ModalContentBody>

        <ModalContentFooter>
          {secondaryButtonProps && (
            <Button
              color="secondary"
              onClick={() => navigateTo(secondaryButtonProps.href)}
            >
              {secondaryButtonProps.text}
            </Button>
          )}

          <Button onClick={() => navigateTo(primaryButtonProps.href)}>
            {primaryButtonProps.text}
          </Button>
        </ModalContentFooter>
      </ModalContent>
    </Modal>
  );
};
